import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { AxiosResponse } from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Oval } from 'react-loader-spinner';
//@ts-ignore
import Modal from 'react-awesome-modal';

import AdminNavbar from '../Navbars/AdminNavbar';
import AdminSidebar from '../Sidebar/AdminSidebar';
import { SuperAdminApis } from '../../apis/superAdminApi';

const formatNaira = (amount: any) =>
    '₦' + Number(amount || 0).toLocaleString('en-NG', { minimumFractionDigits: 2 });

const formatDate = (d?: string | null) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleString('en-NG', { dateStyle: 'medium', timeStyle: 'short' }); }
    catch { return d; }
};

const statusBadgeClass = (s: string) => {
    switch (s) {
        case 'active':    return 'bg-green-100 text-green-700';
        case 'suspended': return 'bg-red-100 text-red-700';
        case 'frozen':    return 'bg-yellow-100 text-yellow-700';
        default:          return 'bg-gray-100 text-gray-700';
    }
};

const txTypeBadgeClass = (t: string) => {
    switch ((t || '').toLowerCase()) {
        case 'credit':
        case 'earning':
            return 'bg-green-100 text-green-700';
        case 'debit':
        case 'withdrawal':
            return 'bg-red-100 text-red-700';
        case 'hold':
        case 'pending':
            return 'bg-yellow-100 text-yellow-700';
        case 'release':
            return 'bg-blue-100 text-blue-700';
        case 'reversal':
        case 'fine':
            return 'bg-orange-100 text-orange-700';
        default:
            return 'bg-gray-100 text-gray-700';
    }
};

export default function AdminWalletDetail() {
    const { userId } = useParams<{ userId: string }>();
    const navigate = useNavigate();
    const userLoginData = useSelector((state: any) => state.data.login.value);

    const { canViewWallets, canManageWallets } = useMemo(() => {
        const role = userLoginData?.data?.role;
        const isAdminRole = role === 'admin' || role === 'super_admin';
        let perms: string[] = [];
        try {
            perms = userLoginData?.data?.permissions
                ? JSON.parse(userLoginData.data.permissions)
                : [];
        } catch {
            perms = [];
        }
        return {
            canViewWallets:   isAdminRole || perms.includes('can_view_wallets'),
            canManageWallets: isAdminRole || perms.includes('can_manage_wallets'),
        };
    }, [userLoginData]);

    const [loader, setLoader] = useState(false);
    const [wallet, setWallet] = useState<any>(null);
    const [profile, setProfile] = useState<any>(null);
    const [transactions, setTransactions] = useState<any>({ data: [] });

    // Modal state
    const [actionModal, setActionModal] = useState(false);
    const [actionLoader, setActionLoader] = useState(false);
    const [reason, setReason] = useState('');

    const load = useCallback((page?: string) => {
        if (!userId || !canViewWallets) return;
        setLoader(true);
        SuperAdminApis.getAdminWalletDetail(Number(userId), { page: page || '1', per_page: '20' })
            .then((res: AxiosResponse<any>) => {
                setLoader(false);
                if (res?.data?.status) {
                    setWallet(res.data.data?.wallet ?? null);
                    setProfile(res.data.data?.profile ?? null);
                    setTransactions(res.data.data?.transactions ?? { data: [] });
                } else {
                    toast.error(res?.data?.message || 'Wallet not found.');
                }
            })
            .catch((err) => {
                setLoader(false);
                if (err?.response?.status === 403) {
                    toast.error(err?.response?.data?.message || 'You do not have permission to view this wallet.');
                } else if (err?.response?.status === 404) {
                    toast.error('Wallet not found.');
                } else {
                    toast.error('Failed to load wallet.');
                }
            });
    }, [userId, canViewWallets]);

    useEffect(() => { load(); }, [load]);

    const openActionModal = () => {
        if (!canManageWallets) {
            toast.error('You do not have permission to manage this wallet.');
            return;
        }
        setReason('');
        setActionModal(true);
    };
    const closeActionModal = () => { setActionModal(false); setReason(''); };

    const submitAction = () => {
        if (!wallet) return;
        const isActive = wallet.status === 'active';
        if (isActive && !reason.trim()) {
            toast.error('Please provide a reason for suspension.');
            return;
        }
        setActionLoader(true);
        SuperAdminApis.toggleAdminWalletStatus(
            Number(wallet.user_id),
            isActive ? { reason: reason.trim() } : undefined
        )
            .then((res: AxiosResponse<any>) => {
                setActionLoader(false);
                if (res?.data?.status) {
                    toast.success(res.data.message || 'Wallet status updated.');
                    closeActionModal();
                    load();
                } else {
                    toast.error(res?.data?.message || 'Could not update wallet status.');
                }
            })
            .catch((err: any) => {
                setActionLoader(false);
                toast.error(err?.response?.data?.message || 'Failed to update wallet status.');
            });
    };

    const paginate = (label: string) => {
        const target = transactions?.links?.find((l: any) => l.label === label);
        if (target?.url) {
            const page = target.url.split('page=')[1];
            if (page) load(page);
        }
    };

    if (!canViewWallets) {
        return (
            <>
                <AdminSidebar title="Wallet Detail" />
                <div className="relative md:ml-64 bg-gray-100 min-h-screen">
                    <AdminNavbar title="Wallet Detail" />
                    <div className="max-w-3xl mx-auto px-6 py-16 text-center">
                        <h2 className="text-xl font-semibold text-gray-800 mb-2">Permission required</h2>
                        <p className="text-gray-600">
                            You don't have permission to view this wallet.
                        </p>
                    </div>
                </div>
            </>
        );
    }

    const txRows: any[] = Array.isArray(transactions?.data) ? transactions.data : [];

    return (
        <>
            <AdminSidebar title="Wallet Detail" />
            <div className="relative md:ml-64 bg-gray-100 min-h-screen">
                <AdminNavbar title="Wallet Detail" />

                <div className="px-4 md:px-10 mx-auto w-full py-6">
                    <button
                        onClick={() => navigate('/admin/wallets')}
                        className="text-sm text-blue-600 hover:text-blue-800 mb-4 inline-flex items-center"
                    >
                        ← Back to Wallets
                    </button>

                    {loader && !wallet && (
                        <div className="bg-white rounded-lg shadow-sm p-10 text-center">
                            <Oval color="#2563eb" secondaryColor="#dbeafe" height={36} width={36} wrapperClass="inline-block" />
                        </div>
                    )}

                    {wallet && (
                        <>
                            {/* Wallet header */}
                            <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
                                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                                    <div>
                                        <div className="flex items-center gap-3 mb-2">
                                            <h2 className="text-2xl font-semibold text-gray-800">
                                                {(wallet.user?.first_name ?? '')} {(wallet.user?.last_name ?? '')}
                                            </h2>
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${statusBadgeClass(wallet.status)}`}>
                                                {wallet.status}
                                            </span>
                                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                                                {wallet.account_type || '—'}
                                            </span>
                                        </div>
                                        <p className="text-sm text-gray-600">{wallet.user?.email ?? '—'}</p>
                                        <p className="text-sm text-gray-600">{wallet.user?.phone_number ?? '—'}</p>
                                        {wallet.status === 'suspended' && (
                                            <p className="text-xs text-red-600 mt-2">
                                                Suspended on {formatDate(wallet.suspended_at)}.
                                                {wallet.suspension_reason ? ' Reason: ' + wallet.suspension_reason : ''}
                                            </p>
                                        )}
                                    </div>
                                    {canManageWallets && (
                                        <button
                                            onClick={openActionModal}
                                            className={`text-sm font-medium px-4 py-2 rounded text-white ${
                                                wallet.status === 'active'
                                                    ? 'bg-red-600 hover:bg-red-700'
                                                    : 'bg-green-600 hover:bg-green-700'
                                            }`}
                                        >
                                            {wallet.status === 'active' ? 'Suspend Wallet' : 'Reactivate Wallet'}
                                        </button>
                                    )}
                                </div>

                                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-6 pt-6 border-t border-gray-100">
                                    <BalanceTile label="Available"    value={formatNaira(wallet.available_balance)} />
                                    <BalanceTile label="Pending"      value={formatNaira(wallet.pending_balance)} />
                                    <BalanceTile label="Total Earned" value={formatNaira(wallet.total_earned)} />
                                    <BalanceTile label="Withdrawn"    value={formatNaira(wallet.total_withdrawn)} />
                                    <BalanceTile label="Reversed"     value={formatNaira(wallet.total_reversed)} />
                                </div>

                                {profile && (
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 pt-4 border-t border-gray-100 text-sm">
                                        <Field label="Rate / Shipment"     value={formatNaira(profile.rate_per_shipment)} />
                                        <Field label="Total Deliveries"     value={profile.total_deliveries ?? 0} />
                                        <Field label="Successful"           value={profile.successful_deliveries ?? 0} />
                                        <Field label="Disputed"             value={profile.disputed_deliveries ?? 0} />
                                    </div>
                                )}
                            </div>

                            {/* Transactions */}
                            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                                <div className="px-6 py-4 border-b border-gray-100">
                                    <h3 className="text-base font-semibold text-gray-800">Transaction History</h3>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-4 py-3 text-left font-semibold text-gray-700">Date</th>
                                                <th className="px-4 py-3 text-left font-semibold text-gray-700">Type</th>
                                                <th className="px-4 py-3 text-right font-semibold text-gray-700">Amount</th>
                                                <th className="px-4 py-3 text-left font-semibold text-gray-700">Description</th>
                                                <th className="px-4 py-3 text-center font-semibold text-gray-700">Status</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {txRows.length === 0 && !loader && (
                                                <tr>
                                                    <td colSpan={5} className="px-4 py-10 text-center text-gray-500">
                                                        No transactions for this wallet.
                                                    </td>
                                                </tr>
                                            )}
                                            {txRows.map((tx: any) => (
                                                <tr key={tx.id} className="hover:bg-gray-50">
                                                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{formatDate(tx.created_at)}</td>
                                                    <td className="px-4 py-3">
                                                        <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${txTypeBadgeClass(tx.type)}`}>
                                                            {tx.type ?? '—'}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3 text-right font-medium text-gray-800">{formatNaira(tx.amount)}</td>
                                                    <td className="px-4 py-3 text-gray-600">{tx.description || tx.notes || tx.reference || '—'}</td>
                                                    <td className="px-4 py-3 text-center text-gray-600 capitalize">{tx.status ?? '—'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {transactions?.links && transactions.links.length > 3 && (
                                    <div className="flex items-center justify-end gap-1 px-4 py-3 border-t border-gray-100">
                                        {transactions.links.map((link: any, idx: number) => (
                                            <button
                                                key={idx}
                                                disabled={!link.url}
                                                onClick={() => paginate(link.label)}
                                                className={`px-3 py-1 rounded text-xs ${
                                                    link.active
                                                        ? 'bg-blue-600 text-white'
                                                        : link.url
                                                            ? 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                                                            : 'bg-gray-50 text-gray-400 border border-gray-100 cursor-not-allowed'
                                                }`}
                                                dangerouslySetInnerHTML={{ __html: link.label }}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Suspend / Reactivate Modal */}
            <Modal visible={actionModal} width="480" effect="fadeInUp" onClickAway={closeActionModal}>
                {wallet && (
                    <div className="p-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-1">
                            {wallet.status === 'active' ? 'Suspend wallet' : 'Reactivate wallet'}
                        </h3>
                        <p className="text-sm text-gray-600 mb-4">
                            {wallet.user?.first_name} {wallet.user?.last_name} — {wallet.account_type || 'Wallet'}
                        </p>

                        {wallet.status === 'active' ? (
                            <>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Reason for suspension <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    rows={4}
                                    placeholder="Explain why this wallet is being suspended…"
                                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-red-500 mb-1"
                                />
                                <p className="text-xs text-gray-500 mb-4">
                                    The rider/fleet will not be able to withdraw while suspended. This action is audited.
                                </p>
                            </>
                        ) : (
                            <p className="text-sm text-gray-700 mb-4">
                                Reactivating will re-enable withdrawals for this wallet. This action is audited.
                            </p>
                        )}

                        <div className="flex justify-end gap-2">
                            <button
                                onClick={closeActionModal}
                                disabled={actionLoader}
                                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={submitAction}
                                disabled={actionLoader}
                                className={`px-4 py-2 text-sm font-medium text-white rounded flex items-center gap-2 ${
                                    wallet.status === 'active'
                                        ? 'bg-red-600 hover:bg-red-700'
                                        : 'bg-green-600 hover:bg-green-700'
                                } disabled:opacity-60`}
                            >
                                {actionLoader && <Oval color="#fff" secondaryColor="#f3f4f6" height={14} width={14} />}
                                {wallet.status === 'active' ? 'Suspend' : 'Reactivate'}
                            </button>
                        </div>
                    </div>
                )}
            </Modal>

            <ToastContainer position="top-right" autoClose={4000} />
        </>
    );
}

function BalanceTile({ label, value }: { label: string; value: any }) {
    return (
        <div>
            <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">{label}</p>
            <p className="text-lg font-semibold text-gray-800">{value}</p>
        </div>
    );
}

function Field({ label, value }: { label: string; value: any }) {
    return (
        <div>
            <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">{label}</p>
            <p className="text-sm text-gray-800">{value}</p>
        </div>
    );
}
